/**
 * How a device output is currently being driven.
 *
 * One enum for every device generation, so nothing downstream branches on device type to interpret an
 * `outputOperatingMode` data point. "Output" rather than "valve" because a port may drive a latching
 * relay for a pump rather than a solenoid.
 *
 * The values are numerically identical to `Gen4ValveOperationMode`
 * (`schemas/gen4InternalStateTracker/subSchemas/gen4ValveInternalState.ts`), which lets a Gen 4 decoder
 * narrow its native 3-bit field with no derivation at all. `Gen4ValveOperationMode` is deliberately not
 * renamed — it has well over a hundred references — so `outputOperatingMode.test.ts` asserts the two
 * agree, and a reorder of either fails loudly rather than silently corrupting the stream.
 *
 * There is intentionally no "automating, subtype unknown" member. Sprout cannot distinguish threshold
 * from duty-cycle automation on the wire, but adding a fifth value for that would be permanent schema
 * debt for a transient firmware limitation, and would eventually need a deprecation plus a backfill
 * decision for historical points. Sprout infers the subtype from configuration instead — see
 * `inferSproutAutomationSubtype` in the backend — which self-heals as Gen 3 retires.
 */
export enum OutputOperatingMode {
    /** Following the downloaded irrigation schedule. Not "idle". */
    SCHEDULE = 0,
    /** Held open or closed by a manual override, from the dashboard or the device's own button. */
    MANUAL = 1,
    /** Sensor-driven automation against a threshold. */
    THRESHOLD_AUTOMATION = 2,
    /** Sensor-driven automation on a duty cycle. */
    DUTY_CYCLE_AUTOMATION = 3,
}

const ALL_MODES: readonly OutputOperatingMode[] = [
    OutputOperatingMode.SCHEDULE,
    OutputOperatingMode.MANUAL,
    OutputOperatingMode.THRESHOLD_AUTOMATION,
    OutputOperatingMode.DUTY_CYCLE_AUTOMATION,
];

/** The inclusive range of legal stored values, for the sensor element's `legalConvertedRange`. */
export const OUTPUT_OPERATING_MODE_RANGE: [number, number] = [
    OutputOperatingMode.SCHEDULE,
    OutputOperatingMode.DUTY_CYCLE_AUTOMATION,
];

/**
 * Narrows a stored data point value to the enum, or `null` when a device reports a value this build
 * does not recognize.
 *
 * Returning `null` rather than throwing or defaulting is deliberate: newer firmware may report a mode
 * added after this build shipped, and a chart should be able to show "unknown mode" for that span
 * instead of failing or silently mislabelling it as SCHEDULE.
 */
export function parseOutputOperatingMode(value: number): OutputOperatingMode | null {
    return ALL_MODES.includes(value as OutputOperatingMode) ? (value as OutputOperatingMode) : null;
}

/**
 * The i18n key for displaying a mode. Kept next to the enum so that adding a mode without giving it a
 * label is a compile error rather than a blank chip in the UI.
 */
export function outputOperatingModeLabelKey(mode: OutputOperatingMode): string {
    switch (mode) {
        case OutputOperatingMode.SCHEDULE:
            return "outputOperatingMode.schedule";
        case OutputOperatingMode.MANUAL:
            return "outputOperatingMode.manual";
        case OutputOperatingMode.THRESHOLD_AUTOMATION:
            return "outputOperatingMode.thresholdAutomation";
        case OutputOperatingMode.DUTY_CYCLE_AUTOMATION:
            return "outputOperatingMode.dutyCycleAutomation";
    }
}

/**
 * Whether the output is being driven by sensor-driven automation of any kind.
 *
 * Exists so callers can ask that question without enumerating the automation subtypes at every call
 * site, which is what would break each time a new automation mode is added.
 */
export function isAutomatedOutputOperatingMode(mode: OutputOperatingMode): boolean {
    return mode === OutputOperatingMode.THRESHOLD_AUTOMATION || mode === OutputOperatingMode.DUTY_CYCLE_AUTOMATION;
}
